/**
 * AppShell — one responsive shell for every role.
 *
 *   • Desktop (lg+): persistent 220px sidebar + main.
 *   • Mobile / tablet (<lg): top bar (brand + bell + online) + a bottom tab
 *     bar of the role's primary destinations + a "More" drawer for the rest
 *     (full nav, install, identity, sign-out).
 *
 * Crew live in here now (no more shell-less /today), so they get navigation,
 * the notifications bell, and a way to sign out — on a phone or an iPad.
 */
import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Download, LogOut, Menu, Wifi, WifiOff, X } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth, type MockRole } from '@/auth/AuthProvider';
import { useCurrentUser } from '@/auth/useCurrentUser';
import { useRecordSession } from '@/api/usage';
import { cn } from '@/lib/cn';
import { Button } from '@/components/ui/Button';
import { NotificationsBell } from '@/components/NotificationsBell';
import { ThemeToggle } from '@/theme/ThemeToggle';
import { useInstallPrompt, useOfflineQueue, useOnlineStatus } from '@/lib/pwa';
import { onIdle, prefetchRoute } from '@/lib/prefetch';
import { navForRole, navSections, type NavItem } from './nav';

function OnlineDot({ online, pending }: { online: boolean; pending: number }) {
  const title = online
    ? pending > 0
      ? `Online — syncing ${pending} queued update${pending === 1 ? '' : 's'}`
      : 'Online'
    : pending > 0
      ? `Offline — ${pending} update${pending === 1 ? '' : 's'} saved on this phone, syncing when you reconnect. Recently viewed screens still work.`
      : 'Offline — task updates are saved on this phone and sync when you reconnect. Recently viewed screens still work.';
  return (
    <div
      title={title}
      className={cn(
        'relative flex items-center justify-center w-6 h-6 rounded-full',
        online ? 'text-status-complete-fg' : 'text-status-blocked-fg',
      )}
    >
      {online ? <Wifi className="w-3.5 h-3.5" /> : <WifiOff className="w-3.5 h-3.5" />}
      {pending > 0 && (
        <span
          aria-label={`${pending} updates waiting to sync`}
          className="absolute -top-0.5 -right-0.5 min-w-3.5 h-3.5 px-0.5 rounded-full bg-status-requested text-status-requested-fg text-[9px] font-bold flex items-center justify-center"
        >
          {pending}
        </span>
      )}
    </div>
  );
}

function SideNav({ items, onNavigate }: { items: NavItem[]; onNavigate?: () => void }) {
  const qc = useQueryClient();
  // Long navs (supervisor/admin) come back as labelled sections; short navs
  // (crew, PM/sales) collapse to a single header-less section — one flat list.
  const sections = navSections(items);
  return (
    <nav className="flex-1 px-2 py-3 overflow-auto">
      {sections.map((section, i) => (
        <div key={section.key ?? 'pinned'} className={cn('space-y-0.5', i > 0 && 'mt-4')}>
          {section.label && (
            <div className="px-2 pb-1 text-[10px] uppercase tracking-wide text-ink-faint">
              {section.label}
            </div>
          )}
          {section.items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              onClick={onNavigate}
              onPointerEnter={() => prefetchRoute(qc, item.to)}
              onFocus={() => prefetchRoute(qc, item.to)}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-2 px-2 py-2 rounded text-sm',
                  isActive
                    ? 'bg-accent/15 text-accent font-medium'
                    : 'text-ink-dim hover:bg-fill-soft hover:text-ink',
                )
              }
            >
              <item.icon className="w-4 h-4" />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </div>
      ))}
    </nav>
  );
}

function MockRoleSwitcher() {
  const { mode, mockRole, setMockRole } = useAuth();
  if (mode !== 'mock') return null;
  const roles: MockRole[] = ['supervisor', 'pm', 'crew', 'sales', 'admin'];
  return (
    <div className="px-3 py-2 border-t border-line">
      <div className="text-[10px] uppercase tracking-wide text-ink-faint mb-1">Mock role (dev)</div>
      <select
        value={mockRole}
        onChange={(e) => setMockRole(e.target.value as MockRole)}
        className="w-full text-xs px-2 py-1 border border-line rounded bg-bg"
      >
        {roles.map((r) => (
          <option key={r} value={r}>
            {r}
          </option>
        ))}
      </select>
    </div>
  );
}

function AccountSection({
  canInstall,
  onInstall,
  displayName,
  role,
  onSignOut,
}: {
  canInstall: boolean;
  onInstall: () => void;
  displayName?: string;
  role?: string;
  onSignOut: () => void;
}) {
  return (
    <>
      {canInstall && (
        <div className="px-3 py-2 border-t border-line">
          <Button variant="outline" size="sm" onClick={onInstall} className="w-full justify-start">
            <Download className="w-3.5 h-3.5" />
            Install app
          </Button>
        </div>
      )}
      <div className="px-3 py-3 border-t border-line space-y-2.5">
        {displayName && (
          <div className="text-xs">
            <div className="font-medium truncate">{displayName}</div>
            <div className="text-ink-faint">{role}</div>
          </div>
        )}
        <div>
          <div className="text-[10px] uppercase tracking-wide text-ink-faint mb-1">Appearance</div>
          <ThemeToggle />
        </div>
        <Button variant="ghost" size="sm" onClick={onSignOut} className="w-full justify-start">
          <LogOut className="w-4 h-4" />
          Sign out
        </Button>
      </div>
    </>
  );
}

export function AppShell() {
  const { signOut } = useAuth();
  const { data: me } = useCurrentUser();
  const qc = useQueryClient();
  // Record this app-load for usage analytics (once, after auth resolves).
  useRecordSession(!!me?.id);
  const online = useOnlineStatus();
  // Mounted once here: auto-replays the offline queue on reconnect (B.1).
  const { pending } = useOfflineQueue();
  const { canInstall, installed, prompt } = useInstallPrompt();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const { visible: visibleNav, primary: primaryNav } = navForRole(me?.role, !!me?.crew_member_id);

  // Warm the role's primary destinations once, when the browser is idle, so even
  // the first click lands on a ready page. A fresh fetch keyed exactly as the
  // page will request — revalidated on mount per staleTime, never a stale paint.
  const warmedRef = useRef(false);
  useEffect(() => {
    if (warmedRef.current || !me?.role) return;
    warmedRef.current = true;
    const primary = primaryNav;
    // Fire-and-forget one-shot: deliberately not cancelled on cleanup so React 18
    // StrictMode's dev-only mount→unmount→remount can't cancel the warm-up before
    // it runs (the ref then blocks a reschedule). prefetchQuery de-dupes, so a
    // double-run would be harmless regardless.
    onIdle(() => {
      for (const item of primary) prefetchRoute(qc, item.to);
    });
  }, [me?.role, primaryNav, qc]);

  async function handleInstall() {
    const outcome = await prompt();
    if (outcome === 'accepted') toast.success('Installed · check your home screen');
    else if (outcome === 'unavailable')
      toast.info('Install not available yet — your browser may already have you covered');
  }

  return (
    <div className="h-full lg:grid lg:grid-cols-[220px_1fr]">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex border-r border-line bg-fill flex-col">
        <div className="px-4 py-4 border-b border-line">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-base font-semibold text-brand">Vari FMS</div>
              <div className="text-[11px] text-ink-faint">
                Scheduler · v1.0{installed && ' · installed'}
              </div>
            </div>
            <div className="flex items-center gap-1">
              <NotificationsBell />
              <OnlineDot online={online} pending={pending} />
            </div>
          </div>
        </div>
        <SideNav items={visibleNav} />
        <MockRoleSwitcher />
        <AccountSection
          canInstall={canInstall}
          onInstall={() => void handleInstall()}
          displayName={me?.display_name}
          role={me?.role}
          onSignOut={() => void signOut()}
        />
      </aside>

      {/* Mobile/tablet column + main content (becomes the grid's 2nd col at lg) */}
      <div className="flex flex-col h-full lg:contents">
        <header className="lg:hidden flex items-center justify-between border-b border-line bg-fill px-4 h-14 shrink-0">
          <div className="text-base font-semibold text-brand">Vari FMS</div>
          <div className="flex items-center gap-1">
            <OnlineDot online={online} pending={pending} />
            <NotificationsBell />
            <button
              onClick={() => setDrawerOpen(true)}
              aria-label="Open menu"
              className="flex items-center justify-center w-9 h-9 rounded hover:bg-fill-soft"
            >
              <Menu className="w-5 h-5" />
            </button>
          </div>
        </header>

        {/* pb-20 clears the tab bar even with the iOS safe-area inset added. */}
        <main className="overflow-auto bg-bg flex-1 pb-20 lg:pb-0">
          <Outlet />
        </main>

        {/* Mobile bottom tab bar. The safe-area padding keeps the tabs out of
            the iPhone home-indicator gesture zone (viewport-fit=cover). */}
        <nav
          className="lg:hidden fixed bottom-0 inset-x-0 z-20 flex border-t border-line bg-fill"
          style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        >
          {primaryNav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              onPointerEnter={() => prefetchRoute(qc, item.to)}
              onFocus={() => prefetchRoute(qc, item.to)}
              className={({ isActive }) =>
                cn(
                  'flex-1 flex flex-col items-center justify-center gap-0.5 py-2 text-[10px]',
                  isActive ? 'text-accent' : 'text-ink-faint',
                )
              }
            >
              <item.icon className="w-5 h-5" />
              <span>{item.label}</span>
            </NavLink>
          ))}
          <button
            onClick={() => setDrawerOpen(true)}
            aria-label="More"
            className="flex-1 flex flex-col items-center justify-center gap-0.5 py-2 text-[10px] text-ink-faint"
          >
            <Menu className="w-5 h-5" />
            <span>More</span>
          </button>
        </nav>
      </div>

      {/* Mobile drawer (full nav + account) */}
      {drawerOpen && (
        <div className="lg:hidden fixed inset-0 z-30">
          <div className="absolute inset-0 bg-black/40" onClick={() => setDrawerOpen(false)} />
          <div className="absolute right-0 top-0 h-full w-72 max-w-[85%] bg-fill border-l border-line flex flex-col">
            <div className="px-4 h-14 border-b border-line flex items-center justify-between shrink-0">
              <div className="text-base font-semibold">Menu</div>
              <button
                onClick={() => setDrawerOpen(false)}
                aria-label="Close menu"
                className="flex items-center justify-center w-9 h-9 rounded hover:bg-fill-soft"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <SideNav items={visibleNav} onNavigate={() => setDrawerOpen(false)} />
            <MockRoleSwitcher />
            <AccountSection
              canInstall={canInstall}
              onInstall={() => void handleInstall()}
              displayName={me?.display_name}
              role={me?.role}
              onSignOut={() => void signOut()}
            />
          </div>
        </div>
      )}
    </div>
  );
}
